import React from "react"
import { composeSx } from "../theme/composeSx"
import { Box, Typography, type BoxProps, type Theme } from "@mui/material"
import { AccentDot } from "./AccentDot"
import { typeScale } from "../theme/tokens"
import { resolveViraPalette } from "../theme/resolveViraPalette"

export type BadgeProps = BoxProps & {
  label: string | number
  tone?: "neutral" | "accent" | "lavender"
}

function toneSx(theme: Theme, tone: NonNullable<BadgeProps["tone"]>) {
  const vira = resolveViraPalette(theme)
  switch (tone) {
    case "neutral":
      return { color: vira.subtle }
    case "accent":
      return { color: vira.accent }
    case "lavender":
      return { color: vira.lavenderText }
  }
}

/** Small count / status badge text (e.g. sidebar "today"). */
export function Badge({
  label,
  tone = "neutral",
  sx,
  ...rest
}: BadgeProps) {
  return (
    <Box
      component="span"
      sx={composeSx(
        () => ({
          display: "inline-flex",
          alignItems: "center",
        }),
        sx,
      )}
      {...rest}
    >
      <Typography
        sx={composeSx((theme) => ({
          fontSize: typeScale.meta,
          ...toneSx(theme, tone),
        }))}
      >
        {label}
      </Typography>
    </Box>
  )
}

export type DotIndicatorProps = BoxProps & {
  active?: boolean
  size?: number
}

/** Active / presence indicator dot. */
export function DotIndicator({
  active = true,
  size = 7,
  sx,
  ...rest
}: DotIndicatorProps) {
  if (!active) {
    return (
      <Box
        aria-hidden
        sx={composeSx(
          (theme) => ({
            width: size,
            height: size,
            borderRadius: "50%",
            border: `1px solid ${resolveViraPalette(theme).border}`,
            display: "inline-block",
          }),
          sx,
        )}
        {...rest}
      />
    )
  }
  return <AccentDot size={size} sx={sx} {...rest} />
}
